import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";

export type SupportStatus = "open" | "in_progress" | "resolved" | "closed";

export interface SupportTicket {
  id: string;
  subject: string;
  status: SupportStatus;
  priority: "low" | "normal" | "high" | "urgent";
  last_message_at: string | null;
  message_count: number;
  requester_unread_count: number;
  created_at: string;
}

export interface SupportMessage {
  id: string;
  author_user_id: string;
  author_name: string;
  body: string;
  attachments?: SupportAttachment[];
  delivery_status: "sending" | "delivered";
  moderation_status: "visible" | "held" | "removed";
  created_at: string;
}

export interface SupportAttachment {
  filename: string;
  content_type: string;
  size_bytes: number;
  object_path: string;
}

interface TicketList {
  items: SupportTicket[];
  total: number;
}

interface MessageList {
  items: SupportMessage[];
  total: number;
}

function websocketUrl(ticketId: string): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  const origin = configured?.startsWith("http")
    ? new URL(configured).origin
    : window.location.origin;
  const url = new URL(`/api/helpdesk/tickets/${ticketId}/ws`, origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function useMySupportRealtime(ticketId: string | null) {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    if (!ticketId) return;
    const token = api.getAccessToken();
    if (!token) return;
    let closed = false;
    let socket: WebSocket | null = null;
    let retry = 800;
    const connect = () => {
      if (closed) return;
      socket = new WebSocket(websocketUrl(ticketId), ["bearer", api.getAccessToken() ?? token]);
      socket.onopen = () => {
        retry = 800;
      };
      socket.onmessage = (event) => {
        try {
          const frame = JSON.parse(String(event.data)) as { type?: string };
          if (frame.type !== "helpdesk.message.created") return;
          void queryClient.invalidateQueries({ queryKey: ["my-support-messages", ticketId] });
          void queryClient.invalidateQueries({ queryKey: ["my-support-tickets"] });
        } catch {
          // A malformed transport frame must not disrupt the thread.
        }
      };
      socket.onclose = () => {
        if (closed) return;
        window.setTimeout(connect, retry);
        retry = Math.min(retry * 1.6, 8000);
      };
    };
    connect();
    return () => {
      closed = true;
      socket?.close();
    };
  }, [queryClient, ticketId]);
}

export function useMySupportTickets() {
  return useQuery<TicketList>({
    queryKey: ["my-support-tickets"],
    queryFn: () => api.get<TicketList>("/api/helpdesk/my/tickets"),
    refetchInterval: 10_000,
  });
}

export function useCreateMySupportTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subject: string;
      body: string;
      requesterName: string;
      priority: "normal" | "high" | "urgent";
    }) =>
      api.post<SupportTicket>("/api/helpdesk/my/tickets", {
        subject: input.subject,
        body: input.body,
        requester_name: input.requesterName,
        priority: input.priority,
        channel: "learner_centre",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["my-support-tickets"] });
    },
  });
}

export function useMySupportMessages(ticketId: string | null) {
  return useQuery<MessageList>({
    queryKey: ["my-support-messages", ticketId],
    queryFn: () => api.get<MessageList>(`/api/helpdesk/my/tickets/${ticketId}/messages`),
    enabled: ticketId !== null,
    refetchInterval: ticketId === null ? false : 5000,
  });
}

export function useSendMySupportMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ticketId: string;
      body: string;
      clientMessageId: string;
      attachments?: SupportAttachment[];
    }) =>
      api.post<SupportMessage>(`/api/helpdesk/my/tickets/${input.ticketId}/messages`, {
        body: input.body,
        client_message_id: input.clientMessageId,
        attachments: input.attachments ?? [],
      }),
    onMutate: async (input) => {
      const queryKey = ["my-support-messages", input.ticketId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MessageList>(queryKey);
      queryClient.setQueryData<MessageList>(queryKey, (current) => ({
        items: [
          ...(current?.items ?? []),
          {
            id: `optimistic:${input.clientMessageId}`,
            author_user_id: "current-user",
            author_name: "You",
            body: input.body,
            attachments: input.attachments ?? [],
            delivery_status: "sending",
            moderation_status: "visible",
            created_at: new Date().toISOString(),
          },
        ],
        total: (current?.total ?? 0) + 1,
      }));
      return { previous, queryKey };
    },
    onError: (_error, _input, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous ?? { items: [], total: 0 });
      }
    },
    onSuccess: (message, input) => {
      queryClient.setQueryData<MessageList>(["my-support-messages", input.ticketId], (current) => ({
        items: (current?.items ?? []).map((item) =>
          item.id === `optimistic:${input.clientMessageId}` ? message : item,
        ),
        total: current?.total ?? 1,
      }));
      void queryClient.invalidateQueries({ queryKey: ["my-support-tickets"] });
    },
  });
}

export function useMarkMySupportRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) =>
      api.post<SupportTicket>(`/api/helpdesk/tickets/${ticketId}/read`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["my-support-tickets"] });
    },
  });
}

const CHAT_FILE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

async function putChatFile(path: string, file: File): Promise<SupportAttachment> {
  if (!CHAT_FILE_TYPES.has(file.type) || file.size > 8 * 1024 * 1024 || file.size < 1) {
    throw new Error("Use an image, PDF, text, Word, or Excel file under 8 MB.");
  }
  const signed = await api.post<{
    put_url: string;
    object_path: string;
    required_headers: Record<string, string>;
  }>(path, {
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
  });
  const target = signed.put_url.startsWith("http")
    ? signed.put_url
    : new URL(signed.put_url, import.meta.env.VITE_API_URL || window.location.origin).toString();
  const uploaded = await fetch(target, {
    method: "PUT",
    headers: signed.required_headers,
    body: file,
  });
  if (!uploaded.ok) throw new Error("The file could not be uploaded.");
  return {
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
    object_path: signed.object_path,
  };
}

export function uploadMySupportFile(ticketId: string, file: File): Promise<SupportAttachment> {
  return putChatFile(`/api/helpdesk/my/tickets/${ticketId}/uploads`, file);
}

export async function openMySupportFile(ticketId: string, objectPath: string): Promise<void> {
  const signed = await api.get<{ get_url: string }>(
    `/api/helpdesk/my/tickets/${ticketId}/downloads?object_path=${encodeURIComponent(objectPath)}`,
  );
  window.open(signed.get_url, "_blank", "noopener,noreferrer");
}
